import { z } from 'zod';

import { cookieSettingsSchema } from '@/schemas/consent';
import { initialUtmSchema } from '@/schemas/utm';
import { type ErrorCode } from '@common/utils';

export const researcherSignupRequestSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
  email: z.email().min(1, 'Email is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  termsAccepted: z.boolean().refine((value) => value === true, {
    message: 'You must accept the Terms & Conditions and Privacy Policy',
  }),
  initialUtm: initialUtmSchema.optional(),
  referralCode: z.string().max(200).optional(),
  captchaToken: z.string().min(1, 'CAPTCHA token is required'),
  captchaV2Token: z.string().optional(),
  // IANA timezone detected on the client; server falls back to a default when absent.
  timezone: z.string().optional(),
  /**
   * The banner decision this browser recorded, or absent if it has none.
   * Researchers have no Profile entity, so core-api does not persist it — it is
   * read there for one thing, resolving the ad-consent grants the server-side
   * `signup_completed` answers to. Absent reads as denied, and no conversion is
   * reported.
   */
  cookieSettings: cookieSettingsSchema.optional(),
});

export type ResearcherSignupRequest = z.infer<
  typeof researcherSignupRequestSchema
>;

export const signupSuccessSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    roles: z.array(z.string()),
  }),
});

export type SignupSuccess = z.infer<typeof signupSuccessSchema>;

/**
 * Narrower than the response: the endpoint returns the whole public user, but
 * the only field the login page acts on is `roles`. Asserting more would turn
 * an unrelated response change into a login outage.
 */
export const loginSuccessSchema = z.object({
  user: z.object({ roles: z.array(z.string()) }),
});

export type LoginSuccess = z.infer<typeof loginSuccessSchema>;

/** `redirect` is only populated for `SSO_REDIRECT_REQUIRED`. */
export class AuthError extends Error {
  readonly code: ErrorCode;
  readonly redirect?: string;

  constructor(code: ErrorCode, message?: string, redirect?: string) {
    super(message ?? code);
    this.code = code;
    this.redirect = redirect;
    this.name = 'AuthError';
  }
}
